/**
 * Export / import de la totalite des donnees.
 *
 * C'est le seul filet de securite d'une application 100 % locale : si le
 * telephone est perdu ou l'app desinstallee, il n'existe aucune copie ailleurs.
 */

import { nowInstant } from '../domain/time'
import type {
  DailyLog,
  Ingredient,
  Meal,
  Recipe,
  Sensation,
  Stool,
} from '../domain/types'
import { META_DERNIER_EXPORT, db, ecrireMeta } from './db'

export const FORMAT_SAUVEGARDE = 'suivi-sci/v2'

export interface Sauvegarde {
  format: typeof FORMAT_SAUVEGARDE
  exporteLe: string
  ingredients: Ingredient[]
  recipes: Recipe[]
  meals: Meal[]
  sensations: Sensation[]
  stools: Stool[]
  dailyLogs: DailyLog[]
}

export async function construireSauvegarde(): Promise<Sauvegarde> {
  const [ingredients, recipes, meals, sensations, stools, dailyLogs] = await Promise.all([
    db.ingredients.toArray(),
    db.recipes.toArray(),
    db.meals.toArray(),
    db.sensations.toArray(),
    db.stools.toArray(),
    db.dailyLogs.toArray(),
  ])
  return {
    format: FORMAT_SAUVEGARDE,
    exporteLe: nowInstant(),
    ingredients,
    recipes,
    meals,
    sensations,
    stools,
    dailyLogs,
  }
}

export function nomFichierSauvegarde(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `suivi-sci-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}.json`
}

export type ResultatExport = 'partage' | 'telecharge' | 'copie' | 'echec'

/**
 * Exporte la sauvegarde par le meilleur canal disponible.
 *
 * En mode standalone sur iOS, un `<a download>` est peu fiable : le fichier
 * s'ouvre parfois dans une vue sans possibilite d'enregistrement, ou ne se
 * declenche pas. On privilegie donc la feuille de partage native, qui permet
 * d'envoyer le fichier vers Fichiers, iCloud ou une messagerie. Le telechargement
 * classique reste le repli sur ordinateur, et la copie dans le presse-papier le
 * dernier recours.
 */
export async function exporterSauvegarde(): Promise<ResultatExport> {
  const sauvegarde = await construireSauvegarde()
  const json = JSON.stringify(sauvegarde, null, 2)
  const nom = nomFichierSauvegarde()

  const marquer = () => ecrireMeta(META_DERNIER_EXPORT, nowInstant())

  // 1. Partage natif avec fichier
  try {
    const fichier = new File([json], nom, { type: 'application/json' })
    const nav = navigator as Navigator & {
      canShare?: (d: ShareData) => boolean
      share?: (d: ShareData) => Promise<void>
    }
    if (nav.share && nav.canShare?.({ files: [fichier] })) {
      await nav.share({ files: [fichier], title: 'Sauvegarde Suivi SCI' })
      await marquer()
      return 'partage'
    }
  } catch (e) {
    // L'utilisateur peut simplement avoir annule la feuille de partage :
    // ce n'est pas une erreur, on tente le canal suivant.
    if ((e as Error)?.name === 'AbortError') return 'echec'
  }

  // 2. Telechargement classique
  try {
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = nom
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
    await marquer()
    return 'telecharge'
  } catch {
    /* on tente le presse-papier */
  }

  // 3. Presse-papier
  try {
    await navigator.clipboard.writeText(json)
    await marquer()
    return 'copie'
  } catch {
    return 'echec'
  }
}

export interface RapportImport {
  ingredients: number
  recipes: number
  meals: number
  sensations: number
  stools: number
  dailyLogs: number
}

function estTableau(v: unknown): v is unknown[] {
  return Array.isArray(v)
}

/**
 * Reimporte une sauvegarde.
 *
 * Mode `fusion` (defaut) : les enregistrements existants de meme identifiant
 * sont ecrases, les autres conserves. Mode `remplacement` : la base est videe
 * au prealable. La fusion est le defaut car elle ne detruit rien.
 */
export async function importerSauvegarde(
  texte: string,
  mode: 'fusion' | 'remplacement' = 'fusion',
): Promise<RapportImport> {
  let donnees: unknown
  try {
    donnees = JSON.parse(texte)
  } catch {
    throw new Error("Le fichier n'est pas un JSON valide.")
  }

  const s = donnees as Partial<Sauvegarde>
  if (!s || typeof s !== 'object' || s.format !== FORMAT_SAUVEGARDE) {
    throw new Error(
      "Ce fichier n'est pas une sauvegarde Suivi SCI (champ « format » absent ou inconnu).",
    )
  }

  const tables = [
    ['ingredients', s.ingredients],
    ['recipes', s.recipes],
    ['meals', s.meals],
    ['sensations', s.sensations],
    ['stools', s.stools],
    ['dailyLogs', s.dailyLogs],
  ] as const

  for (const [nom, valeur] of tables) {
    if (valeur !== undefined && !estTableau(valeur)) {
      throw new Error(`La table « ${nom} » est corrompue.`)
    }
  }

  await db.transaction(
    'rw',
    [db.ingredients, db.recipes, db.meals, db.sensations, db.stools, db.dailyLogs],
    async () => {
      if (mode === 'remplacement') {
        await Promise.all([
          db.ingredients.clear(),
          db.recipes.clear(),
          db.meals.clear(),
          db.sensations.clear(),
          db.stools.clear(),
          db.dailyLogs.clear(),
        ])
      }
      if (s.ingredients?.length) await db.ingredients.bulkPut(s.ingredients)
      if (s.recipes?.length) await db.recipes.bulkPut(s.recipes)
      if (s.meals?.length) await db.meals.bulkPut(s.meals)
      if (s.sensations?.length) await db.sensations.bulkPut(s.sensations)
      if (s.stools?.length) await db.stools.bulkPut(s.stools)
      if (s.dailyLogs?.length) await db.dailyLogs.bulkPut(s.dailyLogs)
    },
  )

  return {
    ingredients: s.ingredients?.length ?? 0,
    recipes: s.recipes?.length ?? 0,
    meals: s.meals?.length ?? 0,
    sensations: s.sensations?.length ?? 0,
    stools: s.stools?.length ?? 0,
    dailyLogs: s.dailyLogs?.length ?? 0,
  }
}

/** Vide integralement la base (hors referentiel reinstalle au demarrage suivant). */
export async function effacerTout(): Promise<void> {
  await db.transaction(
    'rw',
    [db.ingredients, db.recipes, db.meals, db.sensations, db.stools, db.dailyLogs, db.meta],
    async () => {
      await Promise.all([
        db.ingredients.clear(),
        db.recipes.clear(),
        db.meals.clear(),
        db.sensations.clear(),
        db.stools.clear(),
        db.dailyLogs.clear(),
        db.meta.clear(),
      ])
    },
  )
}
